import React, { Component } from "react";
import SEO from "react-seo-component";
import { Col, Container, Row } from "reactstrap";
import ContactUs from "../../components/common/ContactUs";

/**
 * QikTruck Terms and Conditions (South Africa) - Website Terms
 * Notes:
 * - This is a practical, SA-aligned draft suitable for a logistics marketplace website.
 * - You should still have local counsel review for CPA, POPIA, ECT Act, and insurance wording.
 */
class Section extends Component {
  render() {
    const lastUpdated = "January 13, 2025";

    return (
      <React.Fragment>
        <SEO
          title={
            "QikTruck Terms and Conditions - On-demand truck, bakkie and delivery services in South Africa."
          }
          titleTemplate={"QikTruck"}
          titleSeparator={"-"}
          description={
            "QikTruck connects customers with independent transport providers for on-demand deliveries and truck or bakkie hire. Read our Terms and Conditions, cancellations, claims, liability and dispute resolution."
          }
          image={"https://media.qiktruck.co.za/qiktruck-preview-header.png"}
          pathname={"https://qiktruck.co.za/terms"}
          twitterUsername={"qiktrucksa"}
        />

        <section id="home">
          <Container>
            <Row className="justify-content-center">
              <Col lg={12}>
                <div className="mb-4">
                  <div className="page">
                    <div className="translations-content-container">
                      <div className="container">
                        <div
                          className="tab-content translations-content-item en visible"
                          id="en"
                        >
                          <h3>Terms and Conditions</h3>
                          <p>Last updated: {lastUpdated}</p>

                          <p>
                            These Terms and Conditions ("Terms") govern your access to
                            and use of the QikTruck website, mobile applications, and
                            related services (collectively, the "Platform"). By accessing
                            or using the Platform, you agree to these Terms.
                          </p>

                          <h3>1. Interpretation and Definitions</h3>
                          <p>
                            Words with capital letters have the meanings defined below.
                            Headings are for convenience and do not affect interpretation.
                          </p>

                          <h3>2. Definitions</h3>
                          <ul>
                            <li>
                              <p>
                                <strong>Company</strong> means QikTruck, including its
                                successors and permitted assigns.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Country</strong> refers to South Africa.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Customer</strong> means a person or entity that
                                requests, books, or receives a Transport Service through
                                the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Driver / Transport Provider</strong> means an
                                independent third party who provides transport, courier,
                                removal, or related logistics services and makes vehicles
                                available to Customers via the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Booking</strong> means a confirmed order placed by
                                a Customer for a Transport Service via the Platform.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Transport Service</strong> means the transport,
                                delivery, collection, removal, or related service performed
                                by a Driver for a Customer.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Fees</strong> means charges displayed on the
                                Platform, including any platform/service fee, and any other
                                amounts disclosed at checkout or in a contract.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Platform</strong> means the QikTruck website and
                                apps and related services.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Prohibited Goods</strong> means goods we do not
                                allow to be transported through the Platform, including
                                illegal items, hazardous materials, and items listed in
                                Section 9.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>POPIA</strong> means the Protection of Personal
                                Information Act 4 of 2013.
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>CPA</strong> means the Consumer Protection Act 68 of
                                2008.
                              </p>
                            </li>
                          </ul>

                          <h3>3. Marketplace Role and Relationship</h3>
                          <p>
                            QikTruck operates a technology platform that connects Customers
                            with independent Drivers. Unless expressly stated otherwise in
                            writing, QikTruck does not itself provide the Transport Service.
                            The Transport Service is performed by the Driver, who is an
                            independent contractor and not an employee, agent, or
                            representative of QikTruck.
                          </p>
                          <p>
                            QikTruck may provide support, vetting processes, ratings,
                            training modules, and operational standards to improve service
                            quality, but these do not change the independent nature of the
                            Driver relationship. Drivers are not agents of QikTruck and have
                            no authority to bind QikTruck or conclude contracts on QikTruck's
                            behalf.
                          </p>

                          <h4>3.1 Contract of Carriage</h4>
                          <p>
                            A confirmed Booking creates a contract for the Transport Service
                            between the Customer and the Driver. QikTruck does not provide
                            the Transport Service and is not a carrier. QikTruck provides a
                            platform to facilitate the Booking and collects payments as
                            collection agent for the Driver. Nothing in these Terms makes
                            QikTruck a party to the contract of carriage for the Transport
                            Service. Your use of the Platform and any contract for Transport
                            Services are separate. QikTruck's obligations under these Terms
                            relate to the provision of the Platform, not the performance of
                            the Transport Service.
                          </p>

                          <h4>3.2 No Custody of Goods</h4>
                          <p>
                            QikTruck does not take possession of, or assume custody or control
                            over, any goods transported under a Booking. At all times, custody
                            and control of the goods remain with the Customer and the Driver,
                            as between themselves. QikTruck is not a bailee or warehouse
                            operator in respect of any goods.
                          </p>

                          <h3>4. Eligibility and Accounts</h3>
                          <p>
                            You must be at least 18 years old and have legal capacity to
                            enter into contracts to use the Platform. You agree to provide
                            accurate information, keep it updated, and protect your account
                            credentials. You are responsible for activity conducted through
                            your account.
                          </p>

                          <h3>5. Quotes, Bookings, and Availability</h3>
                          <p>
                            Quotes shown on the Platform are estimates based on information
                            you provide (including pickup and delivery locations, distance,
                            item details, timing, and vehicle type). Final pricing may
                            change where:
                          </p>
                          <ul>
                            <li>
                              <p>
                                pickup/delivery details are incorrect or incomplete,
                                including access constraints or waiting time;
                              </p>
                            </li>
                            <li>
                              <p>
                                the load differs materially from what was described (size,
                                weight, number of items, special handling);
                              </p>
                            </li>
                            <li>
                              <p>
                                additional stops are added or routes change; or
                              </p>
                            </li>
                            <li>
                              <p>
                                tolls, permits, or other disclosed pass-through costs apply.
                              </p>
                            </li>
                          </ul>
                          <p>
                            A Booking is only confirmed once accepted by a Driver and
                            confirmed on the Platform. Availability is not guaranteed.
                            QikTruck does not guarantee that a Driver will accept any
                            Booking request made through the Platform.
                          </p>

                          <h3>6. Customer Responsibilities</h3>
                          <p>
                            You agree to:
                          </p>
                          <ul>
                            <li>
                              <p>
                                provide accurate addresses, contact details, and load
                                information;
                              </p>
                            </li>
                            <li>
                              <p>
                                ensure lawful possession of goods and authority to ship them;
                              </p>
                            </li>
                            <li>
                              <p>
                                package goods appropriately unless packaging is included and
                                confirmed in the Booking;
                              </p>
                            </li>
                            <li>
                              <p>
                                ensure safe access for collection and delivery, including
                                parking permissions and building access; and
                              </p>
                            </li>
                            <li>
                              <p>
                                have a responsible person available at pickup and delivery.
                              </p>
                            </li>
                          </ul>

                          <h3>7. Driver Responsibilities</h3>
                          <p>
                            Drivers are independent transport providers who accept Bookings through the
                            Platform. Drivers must perform Transport Services with reasonable care, skill,
                            and diligence, comply with all applicable laws, and meet the operational and
                            safety standards described below.
                          </p>

                          <h4>7.1 Legal and Regulatory Compliance</h4>
                          <ul>
                            <li>
                              <p>
                                Maintain a valid driver’s licence appropriate to the vehicle class and the
                                intended Transport Service, and carry it while operating.
                              </p>
                            </li>
                            <li>
                              <p>
                                Ensure the vehicle is legally registered, roadworthy, and compliant with
                                South African traffic laws and municipal by-laws.
                              </p>
                            </li>
                            <li>
                              <p>
                                Comply with any permit requirements that apply to the service (for example,
                                access permits, delivery restrictions, or controlled access sites), where
                                such permits are within the Driver’s scope to obtain.
                              </p>
                            </li>
                            <li>
                              <p>
                                Not transport Prohibited Goods, and refuse a Booking if the Driver
                                reasonably suspects the goods are unlawful, unsafe, or misrepresented.
                              </p>
                            </li>
                          </ul>

                          <h4>7.2 Vehicle Condition, Safety, and Equipment</h4>
                          <ul>
                            <li>
                              <p>
                                Keep the vehicle in safe operating condition and perform reasonable
                                preventative maintenance (including tyres, brakes, lights, and load-bearing
                                components).
                              </p>
                            </li>
                            <li>
                              <p>
                                Use appropriate securing equipment for the load type (straps, ropes, netting,
                                blankets, corner protectors, pallet straps) and ensure loads are secured to
                                prevent shifting, loss, or damage in transit.
                              </p>
                            </li>
                            <li>
                              <p>
                                Maintain a clean and professional vehicle presentation suitable for customer
                                collections and deliveries.
                              </p>
                            </li>
                            <li>
                              <p>
                                For fridge or temperature-controlled services, maintain and operate the
                                refrigeration unit appropriately and only accept loads that match the
                                booked capability.
                              </p>
                            </li>
                          </ul>

                          <h4>7.3 Service Performance Standards</h4>
                          <p>
                            The Driver is responsible for selecting the route and method of
                            performing the Transport Service, subject to lawful and safe
                            operation of the vehicle.
                          </p>
                          <ul>
                            <li>
                              <p>
                                Arrive within the agreed time window where reasonably possible and promptly
                                notify the Customer and/or QikTruck via the Platform if delayed.
                              </p>
                            </li>
                            <li>
                              <p>
                                Follow the Booking details, including pickup and delivery addresses, contact
                                persons, instructions, and any special handling requirements recorded in the
                                Booking.
                              </p>
                            </li>
                            <li>
                              <p>
                                Use reasonable route planning to complete the service efficiently, taking into
                                account safety, access restrictions, and vehicle suitability.
                              </p>
                            </li>
                            <li>
                              <p>
                                Treat Customers, recipients, and site personnel respectfully and professionally,
                                and avoid abusive, discriminatory, threatening, or harassing conduct.
                              </p>
                            </li>
                          </ul>

                          <h4>7.4 Loading, Handling, and Care of Goods</h4>
                          <ul>
                            <li>
                              <p>
                                Handle goods with reasonable care and in a manner appropriate to the item type,
                                and use protective materials where reasonably necessary to prevent avoidable
                                damage.
                              </p>
                            </li>
                            <li>
                              <p>
                                Refuse to load goods that are unsafe to transport, exceed the safe payload, or
                                cannot be secured adequately.
                              </p>
                            </li>
                            <li>
                              <p>
                                Where the Booking includes loading assistance, ensure adequate manpower is present.
                                If additional help is required beyond what was booked, pause and notify the Customer
                                through the Platform to agree additional costs before proceeding.
                              </p>
                            </li>
                            <li>
                              <p>
                                Take reasonable steps to prevent water damage, dust exposure, or weather exposure
                                where a closed vehicle is booked, or where coverings are reasonably available.
                              </p>
                            </li>
                          </ul>
                          <p>
                            Decisions about how to load, secure and unload the goods are taken
                            by the Driver and the Customer or their representatives on site,
                            not by QikTruck.
                          </p>

                          <h4>7.5 Verification, Proof of Collection and Delivery</h4>
                          <ul>
                            <li>
                              <p>
                                Verify collection and delivery using reasonable measures, which may include OTP,
                                signature, name confirmation, and photos, in accordance with Platform features and
                                privacy requirements.
                              </p>
                            </li>
                            <li>
                              <p>
                                Provide proof of delivery where requested or required by the Booking, excluding
                                prohibited personal information, and only upload proof through approved channels.
                              </p>
                            </li>
                          </ul>

                          <h4>7.6 Communication and Platform Use</h4>
                          <ul>
                            <li>
                              <p>
                                Keep contactable during active Bookings and respond within a reasonable time to
                                Customer and Platform communications relating to the Booking.
                              </p>
                            </li>
                            <li>
                              <p>
                                Use the Platform honestly and accurately, including accepting only Bookings the
                                Driver can realistically fulfil with the listed vehicle and equipment.
                              </p>
                            </li>
                            <li>
                              <p>
                                Not manipulate or falsify location data, timestamps, proofs, or Booking status.
                              </p>
                            </li>
                          </ul>

                          <h4>7.7 Cancellations, No-Shows, and Service Refusals</h4>
                          <ul>
                            <li>
                              <p>
                                Avoid cancellations after acceptance except for legitimate reasons (for example,
                                vehicle breakdown, safety risk, unlawful goods, or access impossibility).
                              </p>
                            </li>
                            <li>
                              <p>
                                If the Driver must cancel, the Driver must promptly notify the Customer and/or
                                QikTruck through the Platform and provide a brief reason.
                              </p>
                            </li>
                            <li>
                              <p>
                                The Driver may refuse a Booking at pickup if goods materially differ from the Booking,
                                are Prohibited Goods, appear unsafe, or cannot be transported legally or safely.
                              </p>
                            </li>
                          </ul>

                          <h4>7.8 Incidents, Accidents, and Claims Cooperation</h4>
                          <ul>
                            <li>
                              <p>
                                Promptly report incidents affecting a Booking (including accidents, theft, load
                                spillage, damage, or delivery failure) through the Platform or to QikTruck support,
                                and provide reasonable supporting information.
                              </p>
                            </li>
                            <li>
                              <p>
                                Cooperate in good faith with reasonable investigations, including providing photos,
                                location details, delivery proofs, and incident descriptions.
                              </p>
                            </li>
                            <li>
                              <p>
                                Where applicable, take reasonable steps to mitigate losses (for example, securing
                                goods after an incident and notifying relevant parties).
                              </p>
                            </li>
                          </ul>

                          <h4>7.9 Insurance and Risk</h4>
                          <ul>
                            <li>
                              <p>
                                Maintain any insurance required by law and any insurance required by the Driver’s
                                own operations. Where goods-in-transit insurance is offered for a Booking, the Driver
                                must comply with the applicable claims procedures and evidence requirements.
                              </p>
                            </li>
                            <li>
                              <p>
                                Immediately disclose to QikTruck any material change that could affect insurability,
                                safety, or the Driver’s ability to perform services (including licence suspension,
                                vehicle unroadworthiness, or serious incidents).
                              </p>
                            </li>
                          </ul>

                          <h4>7.10 Ratings, Quality, and Compliance Actions</h4>
                          <ul>
                            <li>
                              <p>
                                Maintain service quality consistent with Platform standards, including punctuality,
                                communication, and care of goods.
                              </p>
                            </li>
                            <li>
                              <p>
                                Repeated cancellations, poor service ratings, unsafe conduct, fraud indicators, or
                                serious complaints may result in corrective action, retraining requirements,
                                suspension, or removal from the Platform, subject to applicable law and any separate
                                Driver agreement.
                              </p>
                            </li>
                          </ul>

                          <h4>7.11 Subcontracting and Helpers</h4>
                          <ul>
                            <li>
                              <p>
                                The Driver may not transfer a Booking to another driver or vehicle without prior
                                approval through the Platform where such functionality is provided.
                              </p>
                            </li>
                            <li>
                              <p>
                                If the Driver uses helpers, the Driver remains responsible for their conduct and
                                compliance with safety, professionalism, and site rules.
                              </p>
                            </li>
                          </ul>


                          <h3>8. Payment, Fees, VAT, and Receipts</h3>
                          <p>
                            Fees and payment methods will be shown on the Platform. Where
                            applicable, VAT treatment will be indicated. QikTruck may
                            collect payments on behalf of Drivers and may deduct platform
                            fees, service fees, or other agreed charges. QikTruck acts as
                            collection agent for the Driver when receiving payment for the
                            Transport Service. Where QikTruck issues a tax invoice or receipt
                            for the Transport Service, it does so on behalf of the Driver in
                            its capacity as collection agent and not as principal supplier of
                            the Transport Service.
                          </p>
                          <p>
                            If you dispute a charge, you must notify QikTruck promptly and
                            provide relevant details. We may request photos, invoices,
                            call logs, GPS traces, or other supporting information.
                          </p>

                          <h3>9. Prohibited Goods and Special Loads</h3>
                          <p>
                            You may not use the Platform to transport Prohibited Goods,
                            including:
                          </p>
                          <ul>
                            <li>
                              <p>illegal drugs or contraband;</p>
                            </li>
                            <li>
                              <p>weapons, ammunition, explosives, or fireworks;</p>
                            </li>
                            <li>
                              <p>hazardous chemicals, toxic substances, or fuel (unless explicitly permitted and pre-approved);</p>
                            </li>
                            <li>
                              <p>stolen goods;</p>
                            </li>
                            <li>
                              <p>perishable items requiring cold chain unless booked as a fridge service and accepted by a suitable Driver;</p>
                            </li>
                            <li>
                              <p>any item that is unlawful to transport or that requires permits you do not have.</p>
                            </li>
                          </ul>
                          <p>
                            You are responsible for declaring special handling requirements
                            (fragile goods, heavy items, high-value items, machinery, glass,
                            pallets, or bulk loads). Where additional equipment or manpower
                            is required, it must be agreed in the Booking.
                          </p>

                          <h3>9A. Customer Indemnity for Unlawful or Misdeclared Goods</h3>
                          <p>
                            You will be responsible for, and indemnify QikTruck against, any
                            loss, damage, penalty, fine, claim or expense that QikTruck
                            suffers or incurs as a result of your breach of section 6 or 9,
                            including where goods are unlawful, misdeclared, incorrectly
                            described, or not permitted to be transported under these Terms.
                          </p>

                          <h3>10. Delivery Time Windows and Delays</h3>
                          <p>
                            Estimated arrival and delivery times are estimates only and can
                            be affected by traffic, weather, road closures, vehicle issues,
                            and access constraints. QikTruck and Drivers will take
                            reasonable steps to communicate delays where possible.
                          </p>

                          <h3>11. Cancellations, No-Shows, and Waiting Time</h3>
                          <p>
                            Cancellation rules and any cancellation fees will be disclosed
                            on the Platform at the time of Booking or within the relevant
                            service policy. If the Customer is not available, provides an
                            incorrect address, or access is not possible, a no-show or
                            waiting-time fee may apply as displayed or reasonably incurred.
                          </p>

                          <h3>11A. Service Failures and Remedies</h3>
                          <p>
                            If a confirmed Booking is not performed or is cancelled by the
                            Driver without a legitimate reason, QikTruck may facilitate
                            reassignment of the Booking to another Driver if reasonably
                            available, or refund the Fees for that Booking. A refund of Fees
                            is the Customer's sole remedy against QikTruck for non-performance
                            of the Booking. QikTruck does not guarantee that any Booking will
                            be completed within a particular time or at all. This does not
                            limit any rights the Customer may have under the CPA or under
                            contract law to pursue a claim directly against the Driver for
                            losses arising from the Driver's non-performance.
                          </p>

                          <h3>12. Claims, Damage, and Reporting</h3>
                          <p>
                            If goods are damaged or lost, you must report it as soon as
                            reasonably possible, and in any event within the timeframe
                            indicated in the Platform policy or within 48 hours of delivery
                            if no timeframe is indicated. Provide clear photos, description
                            of damage, proof of value, and any other requested information.
                          </p>
                          <p>
                            Claims may be reduced or rejected where damage is caused by
                            inadequate packaging, undisclosed fragility, pre-existing
                            condition, ordinary wear and tear, or where the goods were not
                            as described.
                          </p>

                          <h3>12A. Risk in Goods</h3>
                          <p>
                            Risk of loss of, or damage to, the goods being transported under
                            a Booking remains with the Customer and the Driver, as between
                            themselves. QikTruck does not assume risk in the goods. It is the
                            Customer's responsibility to ensure that appropriate insurance is
                            in place for the goods, whether through the Customer's own
                            insurance or optional goods in transit cover where available.
                          </p>

                          <h3>13. Insurance</h3>
                          <p>
                            Some services may include limited goods-in-transit cover or
                            optional insurance. Coverage, exclusions, and claim procedures
                            will be described in the Platform policy or booking flow if
                            offered. Unless expressly included, insurance is not guaranteed.
                            For high-value loads, you should request and confirm suitable
                            insurance before Booking. QikTruck is not an insurer or
                            underwriter of any risk relating to the Transport Service or
                            the goods and does not provide financial advice in relation to
                            insurance.
                          </p>

                          <h3>14. Limitation of Liability (SA-Aligned)</h3>
                          <p>
                            Nothing in these Terms limits any rights or remedies you may
                            have under the CPA or other applicable South African law that
                            cannot lawfully be excluded.
                          </p>
                          <p>
                            To the maximum extent permitted by law, and subject to the CPA,
                            QikTruck’s liability for direct losses proven to be caused by
                            QikTruck’s breach of these Terms is limited to the Fees paid to
                            QikTruck for the relevant Booking giving rise to the claim.
                          </p>
                          <p>
                            To the maximum extent permitted by law, QikTruck will not be
                            liable for indirect or consequential losses, including loss of
                            profit, loss of business, loss of data, reputational harm, or
                            business interruption, except where such limitation is not
                            permitted by law.
                          </p>
                          <p>
                            Drivers are solely responsible for their performance of the
                            Transport Service. Any claims for non-performance, delay, damage
                            or loss relating to the Transport Service are claims against the
                            Driver. QikTruck may facilitate communication, refunds or dispute
                            handling between the Customer and the Driver, but QikTruck does
                            not indemnify Customers for the Driver's acts or omissions. For
                            clarity, QikTruck is not jointly or severally liable with any
                            Driver for the performance of the Transport Service.
                          </p>
                          <p>
                            Nothing in this Section restricts any rights the Customer may
                            have under the CPA to pursue a claim directly against the Driver.
                          </p>
                          <p>
                            Any claim that you wish to bring against QikTruck under these
                            Terms must be instituted within 12 months after you became aware,
                            or reasonably should have become aware, of the facts giving rise
                            to the claim. After this period, the claim will prescribe and you
                            will no longer have any claim against QikTruck in respect of those
                            facts, except where a longer period is required by law.
                          </p>

                          <h3>15. "As Is" Platform Disclaimer</h3>
                          <p>
                            The Platform is provided on an "as is" and "as available" basis.
                            We do not guarantee uninterrupted or error-free operation. We
                            may perform maintenance and updates and may suspend features to
                            protect users, prevent fraud, or comply with law. We do not
                            guarantee any particular outcome, booking volume, availability
                            of Drivers, or suitability of any Driver or vehicle for your
                            needs.
                          </p>

                          <h3>16. Acceptable Use and Safety</h3>
                          <p>
                            You may not misuse the Platform, including attempting to
                            interfere with platform security, scraping data without
                            permission, providing fraudulent information, or engaging in
                            abusive, discriminatory, or threatening behavior. We may take
                            action including warnings, suspensions, and account termination.
                          </p>

                          <h3>17. Privacy and POPIA</h3>
                          <p>
                            Personal information is processed in accordance with POPIA and
                            our Privacy Policy. By using the Platform, you acknowledge that
                            your information may be shared with Drivers and service
                            providers strictly for fulfilling Bookings, fraud prevention,
                            compliance, and customer support.
                          </p>

                          <h3>18. Third-Party Links and Services</h3>
                          <p>
                            The Platform may contain links to third-party websites or
                            services. QikTruck does not control those third parties and is
                            not responsible for their content or practices. Your use of
                            third-party services is subject to their terms and policies.
                          </p>

                          <h3>19. Suspension and Termination</h3>
                          <p>
                            We may suspend or terminate access to the Platform where we
                            reasonably believe there is fraud, unlawful activity, serious
                            misuse, safety risk, or material breach of these Terms.
                            Where practical, we will provide notice and reasons, except
                            where doing so would compromise safety, fraud prevention, or
                            legal compliance.
                          </p>

                          <h3>20. Dispute Resolution</h3>
                          <p>
                            20.1 If you have a concern or dispute, you agree to first attempt
                            to resolve it informally by contacting QikTruck support with
                            relevant details.
                          </p>
                          <p>
                            20.2 If a dispute with QikTruck arising from these Terms is not
                            resolved within a reasonable time, the dispute may be referred to
                            mediation administered by an agreed mediator or, failing agreement,
                            by the Arbitration Foundation of Southern Africa (AFSA) in
                            Johannesburg.
                          </p>
                          <p>
                            20.3 Subject to any rights you may have under the CPA to approach
                            a court or relevant tribunal or ombud, any dispute between you and
                            QikTruck arising from these Terms that is not resolved by mediation
                            will be finally resolved by arbitration in Johannesburg in
                            accordance with the AFSA rules, by a single arbitrator appointed
                            by AFSA. The arbitration will be conducted in English.
                          </p>
                          <p>
                            20.4 Nothing in this section prevents you from referring a
                            complaint to any applicable regulatory or dispute resolution body
                            in circumstances where you are entitled to do so under the CPA.
                          </p>
                          <p>
                            20.5 To the extent permitted by law, disputes and claims against
                            QikTruck must be brought in your individual capacity and not as
                            part of a group or class action or similar collective proceeding,
                            and may not be consolidated with the claims of any other person,
                            unless QikTruck agrees otherwise in writing.
                          </p>

                          <h3>21. Governing Law and Jurisdiction</h3>
                          <p>
                            These Terms are governed by the laws of South Africa. Subject
                            to section 20 and mandatory consumer protections, the parties
                            consent to the jurisdiction of South African courts for any
                            court proceedings that may arise in relation to these Terms.
                          </p>

                          <h3>22. Changes to These Terms</h3>
                          <p>
                            We may update these Terms from time to time. If changes are
                            material, we will take reasonable steps to notify you through
                            the Platform or by other reasonable means. Your continued use
                            of the Platform after the effective date of updated Terms
                            constitutes acceptance of the updated Terms.
                          </p>

                          <h3>23. Contact Us</h3>
                          <p>
                            If you have questions about these Terms, contact us:
                          </p>
                          <ContactUs />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </React.Fragment>
    );
  }
}

export default Section;